import type { Contract, Membership } from '@kalos/kalos-rpc';

import { CONTRACT_SETTLED_PAYMENT_STATUSES } from '../../../constants';
import { timestamp } from '../../../tools/helpers';
import type { ContractRow, DerivedStatus, MembershipSubRow, PendingReason } from '../types';
import { isAutobilling } from './constants';
import { isRealDate } from './formatDate';

const hasRealCancelDate = (m: Membership): boolean =>
  !!m.cancelEffectiveDate &&
  m.cancelEffectiveDate.trim() !== '' &&
  !m.cancelEffectiveDate.startsWith('0001-01-01');

// Cancelled = a cancel reason, or an inactive row with a concrete cancel timing/effective date.
// Shared by the classifier, the enrollment guard, and the confirm-payment sibling check.
export function isMembershipCancelled(m: Membership): boolean {
  if (m.cancelReason?.trim()) return true;
  return !m.isActive && (hasRealCancelDate(m) || !!m.cancelTiming?.trim());
}

// A contract pending review is not yet activated rather than cancelled; cancelling clears
// review_status alongside is_active, so the pair separates the two.
const isContractCancelled = (contract: Contract): boolean =>
  !contract.isActive && (contract.reviewStatus ?? '').toLowerCase() !== 'pending';

/** The property's membership right now. One cancelled for the end of the period still counts
 * until that date. */
export function isMembershipCurrent(m: Membership): boolean {
  if (m.contract && isContractCancelled(m.contract)) return false;
  if (m.isActive && hasRealCancelDate(m)) return true;
  return !isMembershipCancelled(m);
}

/** The date this row's term runs out: the contract's end date, or the membership's own. */
export function membershipTermEnd(m: Membership, contract: Contract): string | undefined {
  if (isRealDate(contract.dateEnded)) return contract.dateEnded;
  if (isRealDate(m.dateEnded)) return m.dateEnded;
  return undefined;
}

// Autobilling excluded: Helcim charges on a schedule and nothing writes paymentStatus when it does.
const isContractUnpaid = (contract: Contract): boolean =>
  (contract.paymentStatus ?? '').toLowerCase() === 'pending' && !isAutobilling(contract);

// Status for one membership, derived from real state (cancel fields + is_active + contract billing).
// Memberships are created inactive and only flip active when admin review approves the contract, so
// is_active carries the active-vs-pending distinction. A residential row is pending while inactive
// (awaiting confirmation) or while its contract is billed/failed (renewal due / payment failed);
// commercial billing is per-visit, so a billed commercial contract is normal — its outstanding
// shows in the billing rollup, never as a pending sub-row.
function classifyMembership(
  m: Membership,
  contract: Contract,
): {
  status: DerivedStatus;
  pendingReason?: PendingReason;
} {
  if (isMembershipCancelled(m)) {
    return { status: 'cancelled' };
  }
  if (m.isActive && hasRealCancelDate(m)) {
    return { status: 'scheduled_cancel' };
  }
  if (!m.isActive) {
    return { status: 'pending_payment', pendingReason: 'awaiting_confirmation' };
  }
  if (!m.isCommercial) {
    const ps = (contract.paymentStatus ?? '').toLowerCase();
    if (ps === 'billed') return { status: 'pending_payment', pendingReason: 'renewal_due' };
    if (ps === 'failed') return { status: 'pending_payment', pendingReason: 'payment_failed' };
  }
  return { status: 'active' };
}

/**
 * Terminal for the current term: the row is done and owes nothing further, whether it was cancelled,
 * superseded by a renewal or ran out. Money and scheduling rollups drop these rows.
 */
export const isClosedOut = (s: DerivedStatus): boolean =>
  s === 'cancelled' || s === 'renewed' || s === 'expired';

/** The contract still owes money staff collect by hand: the Confirm Payment offer. */
export const canConfirmPayment = (row: ContractRow): boolean =>
  !isClosedOut(row.status) &&
  (row.outstandingTotal > 0 || row.memberships.some((s) => s.awaitingManualPayment));

/**
 * True while a membership has never been activated — the same state under either label it can carry:
 * Pending Payment when the upfront is still outstanding, Awaiting Confirmation once it's collected.
 * Callers that care about "never activated" rather than "unpaid" must use this, not a status compare.
 */
export const isPendingActivation = (s: {
  status: DerivedStatus;
  pendingReason?: PendingReason;
}): boolean =>
  s.status === 'awaiting_confirmation' ||
  (s.status === 'pending_payment' && s.pendingReason === 'awaiting_confirmation');

/**
 * Active, but its stored status still reads Awaiting Confirmation — a plan created already active
 * (migrated), which approval never relabels. The only plans Mark Active may touch.
 */
export const isStuckAwaitingConfirmation = (
  sub: MembershipSubRow,
  statuses: Map<number, string> | undefined,
): boolean =>
  sub.membership.isActive &&
  statuses?.get(sub.membership.statusId) === 'awaiting_confirmation' &&
  !isMembershipCancelled(sub.membership);

// Per-membership status: stored status_id when set, else derived fallback; billing overlay stays FE-derived.
export function mapMembershipStatus(
  m: Membership,
  contract: Contract | undefined,
  statusMap?: Map<number, string>,
): { status: DerivedStatus; pendingReason?: PendingReason } {
  if (!contract) {
    return { status: 'unknown' };
  }
  // Only the renewal close-out writes this status, so it outranks the contract-inactive evidence
  // below that would otherwise read a superseded term as cancelled.
  if (m.statusId && statusMap?.get(m.statusId) === 'renewed') {
    // A renewed term keeps covering until its own end date.
    const termEnd = membershipTermEnd(m, contract);
    return termEnd && termEnd.slice(0, 10) >= timestamp(true)
      ? { status: 'active' }
      : { status: 'renewed' };
  }
  // Cancel evidence outranks the stored status_id — nothing here writes it, so it goes stale.
  if (isContractCancelled(contract)) {
    return { status: 'cancelled' };
  }
  if (m.isActive && hasRealCancelDate(m)) {
    return { status: 'scheduled_cancel' };
  }
  if (isMembershipCancelled(m)) {
    return { status: 'cancelled' };
  }
  const stored = m.statusId ? statusMap?.get(m.statusId) : undefined;
  let base: { status: DerivedStatus; pendingReason?: PendingReason };
  if (stored) {
    base = { status: stored as DerivedStatus };
    // A stored Pending Payment carries no reason, and the badge needs one: an active membership
    // got there from a declined renewal, an inactive one still owes its enrollment.
    if (base.status === 'pending_payment') {
      base.pendingReason = m.isActive ? 'payment_failed' : 'awaiting_confirmation';
    }
  } else {
    base = classifyMembership(m, contract);
  }
  // Not yet approved: Pending Payment while the upfront is outstanding, else Awaiting Confirmation.
  if (isPendingActivation(base)) {
    return isContractUnpaid(contract)
      ? { status: 'pending_payment', pendingReason: 'awaiting_confirmation' }
      : { status: 'awaiting_confirmation' };
  }
  const termEnd = membershipTermEnd(m, contract);
  if (m.isActive && termEnd && termEnd.slice(0, 10) < timestamp(true) && !isAutobilling(contract)) {
    return { status: 'expired' };
  }
  if (base.status === 'active' && !m.isCommercial) {
    const ps = (contract.paymentStatus ?? '').toLowerCase();
    if (ps === 'billed') return { status: 'pending_payment', pendingReason: 'renewal_due' };
    if (ps === 'failed') return { status: 'pending_payment', pendingReason: 'payment_failed' };
  }
  // Approved, but the contract's upfront is still unpaid.
  if (base.status === 'active' && m.isActive && !m.isCommercial && isContractUnpaid(contract)) {
    return { status: 'pending_payment' };
  }
  // Approved ahead of the contract's start date.
  if (
    base.status === 'active' &&
    m.isActive &&
    isRealDate(contract.dateStarted) &&
    contract.dateStarted.slice(0, 10) > timestamp(true)
  ) {
    return { status: 'not_active_yet' };
  }
  return base;
}

// Contract-level status from contract.paymentStatus — used only for a contract with no memberships.
function statusFromPaymentStatus(contract: Contract): {
  status: DerivedStatus;
  pendingReason?: PendingReason;
} {
  const ps = (contract.paymentStatus ?? '').toLowerCase();
  if (ps === 'canceled' || ps === 'cancelled') return { status: 'cancelled' };
  if (ps === 'pending')
    return { status: 'pending_payment', pendingReason: 'awaiting_confirmation' };
  if (ps === 'billed') return { status: 'pending_payment', pendingReason: 'renewal_due' };
  if (ps === 'failed') return { status: 'pending_payment', pendingReason: 'payment_failed' };
  if (
    CONTRACT_SETTLED_PAYMENT_STATUSES.some((s) => s.toLowerCase() === ps) ||
    ps === 'no charge' ||
    ps === ''
  ) {
    return { status: 'active' };
  }
  return { status: 'unknown' };
}

// Pass one membership for a sub-row or all of a contract's for the contract row; cancelled rows are
// dropped before aggregating, so a stale contract.paymentStatus can't override live memberships.
export function mapContractStatus(
  contract: Contract | undefined,
  memberships: Membership[],
  opts?: { isContractLevel?: boolean; statusMap?: Map<number, string> },
): { status: DerivedStatus; pendingReason?: PendingReason } {
  if (!contract) {
    return { status: 'unknown' };
  }
  // Same derivation the sub-rows use, so a contract row and its property rows can't disagree.
  const classified = memberships.map((m) => mapMembershipStatus(m, contract, opts?.statusMap));
  const live = classified.filter((s) => !isClosedOut(s.status));
  // Cancel clears is_active but not review_status, so a cancelled contract keeps a stale 'pending'
  // flag. Live memberships are the source of truth: once they're all cancelled the contract is
  // cancelled, and must leave Admin Review rather than let the stale gate pin it there.
  const fullyCancelled = memberships.length > 0 && live.length === 0;

  const pendingReview = (contract.reviewStatus ?? '').toLowerCase() === 'pending';
  // Unpaid outranks review: approving a contract collects none of its money, so the badge carries the
  // money signal at every level and ContractRow.needsReview drives the Admin Review tab instead. A
  // contract still pending review was never activated, so its is_active of 0 is not a cancellation.
  if (!fullyCancelled && (contract.isActive || pendingReview) && isContractUnpaid(contract)) {
    return { status: 'pending_payment', pendingReason: 'awaiting_confirmation' };
  }
  // Contract row only, never a sub-row. Reached only when paid — unpaid took the branch above.
  if (opts?.isContractLevel && !fullyCancelled && pendingReview) {
    return { status: 'admin_review' };
  }
  if (memberships.length === 0) {
    return !contract.isActive ? { status: 'cancelled' } : statusFromPaymentStatus(contract);
  }

  // Superseded by a renewal: the close-out stamped the memberships and left nothing current behind,
  // so the contract reads Renewed rather than falling through to the cancelled fallback below.
  if (
    classified.some((s) => s.status === 'renewed') &&
    !classified.some(
      (s) =>
        s.status === 'active' ||
        s.status === 'awaiting_confirmation' ||
        s.status === 'pending_payment' ||
        s.status === 'not_active_yet',
    )
  ) {
    return { status: 'renewed' };
  }
  if (live.length === 0 && classified.some((s) => s.status === 'expired')) {
    return { status: 'expired' };
  }
  if (live.length === 0 || !contract.isActive) {
    return { status: 'cancelled' };
  }
  const pending = live.find((s) => s.status === 'pending_payment');
  if (pending) {
    return { status: 'pending_payment', pendingReason: pending.pendingReason };
  }
  if (live.some((s) => s.status === 'awaiting_confirmation')) {
    return { status: 'awaiting_confirmation' };
  }
  if (live.some((s) => s.status === 'active')) {
    return { status: 'active' };
  }
  if (live.some((s) => s.status === 'not_active_yet')) {
    return { status: 'not_active_yet' };
  }
  if (live.every((s) => s.status === 'scheduled_cancel')) {
    return { status: 'scheduled_cancel' };
  }
  return { status: 'unknown' };
}
